import {
  displayRulesSchema,
  getOptionType,
  productOptionChoiceSchema,
  type DisplayRule,
  type ProductOptionChoice,
  type ProductOptionType,
} from '@tkpod/shared';

export interface ProductOptionRow {
  id: string;
  key: string;
  displayName: string;
  description: string | null;
  optionType: ProductOptionType;
  role: 'ADDON' | 'VARIANT_SELECTOR';
  helpText: string | null;
  placeholder: string | null;
  defaultValue: string | null;
  isRequired: boolean;
  isActive: boolean;
  config: unknown;
  values: unknown;
  displayRules: unknown;
  boundOptionTypeId: string | null;
  lineItemLabel: string | null;
  showInStorefront: boolean;
  showInPortal: boolean;
  showToProvider: boolean;
  hiddenInCart: boolean;
  priceDeltaMode: 'NONE' | 'PER_UNIT' | 'PER_LINE' | 'PERCENT_OF_VARIANT';
  priceDeltaCents: number | null;
  priceDeltaPercent: number | string | null;
  sortOrder: number;
}

export interface OptionWritePayload {
  key: string;
  displayName: string;
  optionType: string;
  role: 'ADDON' | 'VARIANT_SELECTOR';
  description?: string;
  helpText?: string;
  placeholder?: string;
  defaultValue?: string;
  isRequired: boolean;
  isActive: boolean;
  config: unknown;
  values: ProductOptionChoice[];
  displayRules: DisplayRule[] | null;
  boundOptionTypeId?: string;
  lineItemLabel?: string;
  showInStorefront: boolean;
  showInPortal: boolean;
  showToProvider: boolean;
  hiddenInCart: boolean;
  priceDeltaMode: ProductOptionRow['priceDeltaMode'];
  priceDeltaCents?: number;
  priceDeltaPercent?: number;
}

export function choicesOf(value: unknown): ProductOptionChoice[] {
  if (!Array.isArray(value)) return [];
  const kept: ProductOptionChoice[] = [];
  for (const row of value) {
    const parsed = productOptionChoiceSchema.safeParse(row);
    if (parsed.success) kept.push(parsed.data);
  }
  return kept;
}

export function rulesOf(value: unknown): DisplayRule[] {
  const parsed = displayRulesSchema.safeParse(value ?? []);
  return parsed.success ? parsed.data : [];
}

export function payloadFromRow(
  row: ProductOptionRow,
  patch: Partial<OptionWritePayload> = {},
): OptionWritePayload {
  const percent =
    row.priceDeltaPercent == null || row.priceDeltaPercent === ''
      ? undefined
      : Number(row.priceDeltaPercent);
  return {
    key: row.key,
    displayName: row.displayName,
    optionType: row.optionType,
    role: row.role,
    description: row.description ?? undefined,
    helpText: row.helpText ?? undefined,
    placeholder: row.placeholder ?? undefined,
    defaultValue: row.defaultValue ?? undefined,
    isRequired: row.isRequired,
    isActive: row.isActive,
    config: row.config ?? getOptionType(row.optionType)?.defaultConfig ?? {},
    values: choicesOf(row.values),
    displayRules: row.displayRules == null ? null : rulesOf(row.displayRules),
    boundOptionTypeId: row.boundOptionTypeId ?? undefined,
    lineItemLabel: row.lineItemLabel ?? undefined,
    showInStorefront: row.showInStorefront,
    showInPortal: row.showInPortal,
    showToProvider: row.showToProvider,
    hiddenInCart: row.hiddenInCart,
    priceDeltaMode: row.priceDeltaMode,
    priceDeltaCents: row.priceDeltaCents ?? undefined,
    priceDeltaPercent: Number.isFinite(percent) ? percent : undefined,
    ...patch,
  };
}

export function rowFromPayload(
  existing: ProductOptionRow | null,
  payload: OptionWritePayload,
): ProductOptionRow {
  return {
    id: existing?.id ?? 'draft',
    key: payload.key,
    displayName: payload.displayName,
    description: payload.description ?? null,
    optionType: payload.optionType as ProductOptionRow['optionType'],
    role: payload.role,
    helpText: payload.helpText ?? null,
    placeholder: payload.placeholder ?? null,
    defaultValue: payload.defaultValue ?? null,
    isRequired: payload.isRequired,
    isActive: payload.isActive,
    config: payload.config,
    values: payload.values,
    displayRules: payload.displayRules,
    boundOptionTypeId: payload.boundOptionTypeId ?? null,
    lineItemLabel: payload.lineItemLabel ?? null,
    showInStorefront: payload.showInStorefront,
    showInPortal: payload.showInPortal,
    showToProvider: payload.showToProvider,
    hiddenInCart: payload.hiddenInCart,
    priceDeltaMode: payload.priceDeltaMode,
    priceDeltaCents: payload.priceDeltaCents ?? null,
    priceDeltaPercent: payload.priceDeltaPercent ?? null,
    sortOrder: existing?.sortOrder ?? 0,
  };
}

export function slugOptionKey(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 63);
  return slug.length > 0 ? slug : 'option';
}
